import { Bell, Bot, User } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader } from '~/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { useCostActual } from '~/features/cost-estimate/hooks/use-cost-estimate';
import type { PlannedCost } from '~/features/cost-estimate/types/cost-estimate.types';
import { formatUsd } from '~/features/cost-estimate/utils/format-usd';
import { useShowCostAmounts } from '~/hooks/useCanSeeCost';
import { cn } from '~/utils/cn';
import { formatDate, formatDateTime } from '~/utils/date';

import type {
  BatchFunnel,
  FunnelComparisonRow,
  FunnelProductsQuery,
  FunnelUsage,
} from '../../types/batch-funnel.api.types';
import { formatFunnelCount, formatFunnelUsd, unavailableByField } from '../../utils/batch-funnel';
import type { StageErrorTally } from '../../utils/stage-errors';

import { FunnelOutcomes } from './funnel-outcomes';
import { FunnelStages } from './funnel-stages';

/** A comparison drop this large (share of the previous count) is "where it happened". */
const LARGE_DROP_RATIO = 0.2;

/** `snake_case` server step key → readable words; unknown keys still render (§4.9). */
function humanizeKey(key: string): string {
  const words = key.replace(/[_~]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** First stage whose count fell by ≥ {@link LARGE_DROP_RATIO} vs the previous batch. */
function firstLargeDrop(rows: FunnelComparisonRow[]): string | null {
  const hit = rows.find(
    (r) =>
      r.delta !== null &&
      r.delta < 0 &&
      r.previous !== null &&
      r.previous > 0 &&
      Math.abs(r.delta) / r.previous >= LARGE_DROP_RATIO,
  );
  return hit?.key ?? null;
}

interface FunnelRunDetailsProps {
  funnel: BatchFunnel;
  /** Workflow id the page's Cost estimate strip reads — shared so both show one cost. */
  costWorkflowId: string | null;
  /** The cost planned at creation (batch header), shown first in the Cost card when stored. */
  plannedCost?: PlannedCost | null;
  /** Current stage errors per funnel stage key — ⛔ / ⚠ chips on the stage cards. */
  errorTally?: Map<string, StageErrorTally>;
  /** Opens the page's products sheet for a stage reason or outcome row. */
  onDrill: (title: string, query: FunnelProductsQuery) => void;
}

/**
 * "Run details" tab of the batch detail page — the product funnel and where
 * every product ended up (both drill into the products sheet), then quality
 * before → after and cost, the comparison with the previous batch, per-market
 * results, side branches (sample, experiment arms, consolidation, feed audit;
 * never in the totals), the activity timeline and alert events. A part the
 * server couldn't answer shows "Not available yet — {reason}". Cost (the card and
 * each stage's $) follows the funnel's `costHidden` when sent, else lily.ai-only.
 */
export function FunnelRunDetails({
  funnel,
  costWorkflowId,
  plannedCost = null,
  onDrill,
  errorTally,
}: FunnelRunDetailsProps) {
  const unavailable = unavailableByField(funnel.unavailable);
  const side = funnel.sideBranches;
  const showCost = useShowCostAmounts(funnel.costHidden);
  return (
    <div className="space-y-6" data-testid="batch-funnel-run-details-panel">
      <FunnelStages
        stages={funnel.stages ?? []}
        onDrill={onDrill}
        showCost={showCost}
        {...(errorTally ? { errorTally } : {})}
      />
      <FunnelOutcomes
        outcomes={funnel.outcomes ?? []}
        outcomesCheck={funnel.outcomesCheck}
        stages={funnel.stages ?? []}
        onDrill={onDrill}
      />
      <div className={cn('grid gap-6', showCost && 'lg:grid-cols-2')}>
        <QualitySection funnel={funnel} unavailableReason={unavailable.get('quality')} />
        {showCost && (
          <CostSection
            funnel={funnel}
            costWorkflowId={costWorkflowId}
            plannedCost={plannedCost}
            unavailableReason={unavailable.get('cost')}
          />
        )}
      </div>
      <ComparisonSection funnel={funnel} unavailableReason={unavailable.get('comparison')} />
      <MarketsSection funnel={funnel} unavailableReason={unavailable.get('markets')} />
      {side && (
        <SideBranchesSection
          funnel={funnel}
          feedAuditUnavailable={unavailable.get('sideBranches.feedAudit')}
        />
      )}
      <ActivitySection funnel={funnel} />
    </div>
  );
}

/** Card shell shared by every Run details section. */
function Section({
  title,
  subtitle,
  testId,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Root `data-testid`; the heading gets `${testId}-title`. */
  testId: string;
  children: ReactNode;
}) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="p-6 pb-3">
        <h2 className="text-xl" data-testid={`${testId}-title`}>
          {title}
        </h2>
        {subtitle && <p className="text-muted-foreground text-sm font-light">{subtitle}</p>}
      </CardHeader>
      <CardContent className="px-6 pb-6">{children}</CardContent>
    </Card>
  );
}

/** "Not available yet — {reason}" line; never rendered as a 0. */
function NotAvailable({ reason, testId }: { reason: string | undefined; testId: string }) {
  const { t } = useTranslation('catalog-operations');
  return (
    <p className="text-muted-foreground text-sm" data-testid={testId}>
      {reason
        ? t('batchFunnel.runDetails.notAvailableReason', { reason })
        : t('batchFunnel.runDetails.notAvailable')}
    </p>
  );
}

/** Label / value grid used by the quality, cost and side-branch facts. */
function FactGrid({
  facts,
  testId,
}: {
  facts: { id: string; label: string; value: string }[];
  /** Grid `data-testid`; each fact gets `${testId}-${id}` and its value `${testId}-${id}-value`. */
  testId: string;
}) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3" data-testid={testId}>
      {facts.map((fact) => (
        <div key={fact.label} className="min-w-0" data-testid={`${testId}-${fact.id}`}>
          <dt className="text-muted-foreground text-xs">{fact.label}</dt>
          <dd
            className="text-foreground text-lg font-semibold tabular-nums"
            data-testid={`${testId}-${fact.id}-value`}
          >
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Quality before → after (0–100). Null while running or when nothing was scored. */
function QualitySection({
  funnel,
  unavailableReason,
}: {
  funnel: BatchFunnel;
  unavailableReason: string | undefined;
}) {
  const { t } = useTranslation('catalog-operations');
  const quality = funnel.quality;
  const score = (n: number | null) => (n === null ? '—' : n.toFixed(1));
  return (
    <Section title={t('batchFunnel.runDetails.quality.title')} testId="batch-funnel-quality-card">
      {quality ? (
        <FactGrid
          testId="batch-funnel-quality-fact"
          facts={[
            {
              id: 'before',
              label: t('batchFunnel.runDetails.quality.before'),
              value: score(quality.before),
            },
            {
              id: 'after',
              label: t('batchFunnel.runDetails.quality.after'),
              value: score(quality.after),
            },
            {
              id: 'delta',
              label: t('batchFunnel.runDetails.quality.delta'),
              value:
                quality.delta === null
                  ? '—'
                  : `${quality.delta > 0 ? '+' : ''}${quality.delta.toFixed(1)}`,
            },
            {
              id: 'scored',
              label: t('batchFunnel.runDetails.quality.scored'),
              value: `${formatFunnelCount(quality.preScored)} / ${formatFunnelCount(quality.preExpected)}`,
            },
            {
              id: 'below-threshold',
              label: t('batchFunnel.runDetails.quality.belowThreshold'),
              value: formatFunnelCount(quality.belowThreshold),
            },
          ]}
        />
      ) : (
        <NotAvailable
          reason={unavailableReason ?? t('batchFunnel.runDetails.quality.pending')}
          testId="batch-funnel-quality-unavailable"
        />
      )}
    </Section>
  );
}

/** Funnel usage only when agent telemetry was recorded — `productsWithUsage: 0`
 *  comes back as zeros for runs that predate it, which must not read as "0 tokens". */
function recordedUsage(usage: FunnelUsage | null | undefined): FunnelUsage | null {
  return usage && usage.productsWithUsage > 0 ? usage : null;
}

/**
 * Batch cost. Dollars come from the cost ledger (`cost-estimates/{id}/actual`) —
 * the same read as the page's Cost estimate strip, so the two never disagree —
 * and fall back to the funnel's `cost` only when the ledger has nothing. Token
 * counts come from the funnel's agent telemetry and show only when recorded.
 */
function CostSection({
  funnel,
  costWorkflowId,
  plannedCost,
  unavailableReason,
}: {
  funnel: BatchFunnel;
  costWorkflowId: string | null;
  plannedCost: PlannedCost | null;
  unavailableReason: string | undefined;
}) {
  const { t } = useTranslation('catalog-operations');
  // Same query key as the strip's `useCostActual`, so this adds no request once
  // the strip has fetched. Spend only exists once enrichment has started.
  const enrichmentStarted =
    funnel.stages?.some((s) => s.key === 'ENRICHED' && s.startedAt !== null) ?? false;
  const { data: actual } = useCostActual(costWorkflowId, { enabled: enrichmentStarted });
  const cost = funnel.cost;
  const enrichmentUsage = recordedUsage(cost?.enrichment);
  const scoringUsage = recordedUsage(cost?.scoring);

  const facts: { id: string; label: string; value: string }[] = [];
  // Planned first (stored at creation), so a batch shows its cost before any spend exists.
  if (plannedCost?.totalUsd != null) {
    facts.push({
      id: 'planned',
      label: t('batchFunnel.runDetails.cost.planned'),
      value: formatUsd(plannedCost.totalUsd),
    });
  }
  if (actual) {
    facts.push(
      {
        id: 'total',
        label: t('batchFunnel.runDetails.cost.total'),
        value: formatUsd(actual.totalUsd),
      },
      {
        id: 'enrichment',
        label: t('batchFunnel.runDetails.cost.enrichment'),
        value: formatUsd(actual.enrichmentUsd),
      },
    );
    if (actual.searchDescriptorsUsd !== null) {
      facts.push({
        id: 'search-descriptors',
        label: t('batchFunnel.runDetails.cost.searchDescriptors'),
        value: formatUsd(actual.searchDescriptorsUsd),
      });
    }
    if (actual.scoringUsd !== null) {
      facts.push({
        id: 'scoring',
        label: t('batchFunnel.runDetails.cost.scoring'),
        value: formatUsd(actual.scoringUsd),
      });
    }
    facts.push({
      id: 'products-with-cost',
      label: t('batchFunnel.runDetails.cost.productsWithCost'),
      value: t('batchFunnel.runDetails.cost.productsOf', {
        count: actual.productsWithCost,
        total: actual.enrichedProducts.toLocaleString(),
      }),
    });
  } else if (cost?.totalCostUsd != null) {
    facts.push({
      id: 'total',
      label: t('batchFunnel.runDetails.cost.total'),
      value: formatFunnelUsd(cost.totalCostUsd),
    });
    if (enrichmentUsage?.costUsd != null) {
      facts.push({
        id: 'enrichment',
        label: t('batchFunnel.runDetails.cost.enrichment'),
        value: formatFunnelUsd(enrichmentUsage.costUsd),
      });
    }
    if (scoringUsage?.costUsd != null) {
      facts.push({
        id: 'scoring',
        label: t('batchFunnel.runDetails.cost.scoring'),
        value: formatFunnelUsd(scoringUsage.costUsd),
      });
    }
  }
  if (enrichmentUsage) {
    facts.push({
      id: 'enrichment-tokens',
      label: t('batchFunnel.runDetails.cost.enrichmentTokens'),
      value: (enrichmentUsage.inputTokens + enrichmentUsage.outputTokens).toLocaleString(),
    });
  }
  if (scoringUsage) {
    facts.push(
      {
        id: 'scoring-tokens',
        label: t('batchFunnel.runDetails.cost.scoringTokens'),
        value: (scoringUsage.inputTokens + scoringUsage.outputTokens).toLocaleString(),
      },
      {
        id: 'scoring-calls',
        label: t('batchFunnel.runDetails.cost.scoringCalls'),
        value: (cost?.scoringCalls ?? 0).toLocaleString(),
      },
    );
  }

  return (
    <Section
      title={t('batchFunnel.runDetails.cost.title')}
      subtitle={t('batchFunnel.runDetails.cost.subtitle')}
      testId="batch-funnel-cost-card"
    >
      {facts.length > 0 ? (
        <FactGrid facts={facts} testId="batch-funnel-cost-fact" />
      ) : (
        <NotAvailable
          reason={unavailableReason ?? t('batchFunnel.runDetails.cost.notRecorded')}
          testId="batch-funnel-cost-unavailable"
        />
      )}
    </Section>
  );
}

/** This batch vs the previous one of the same kind; the first big drop is highlighted. */
function ComparisonSection({
  funnel,
  unavailableReason,
}: {
  funnel: BatchFunnel;
  unavailableReason: string | undefined;
}) {
  const { t } = useTranslation('catalog-operations');
  const comparison = funnel.comparison;
  if (!comparison && !unavailableReason) return null;
  const rows = comparison?.stages ?? [];
  const dropKey = firstLargeDrop(rows);
  const stageLabel = (key: string | null) =>
    funnel.stages?.find((s) => s.key === key)?.label ?? key ?? '—';
  return (
    <Section
      title={t('batchFunnel.runDetails.comparison.title')}
      testId="batch-funnel-comparison-card"
      {...(comparison?.previousName || comparison?.previousCreatedAt
        ? {
            subtitle: t('batchFunnel.runDetails.comparison.subtitle', {
              name: comparison.previousName ?? comparison.previousBatchId ?? '—',
              date: comparison.previousCreatedAt ? formatDate(comparison.previousCreatedAt) : '—',
            }),
          }
        : {})}
    >
      {comparison && rows.length > 0 ? (
        <Table data-testid="batch-funnel-comparison-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('batchFunnel.outcomes.colStage')}</TableHead>
              <TableHead className="text-right">
                {t('batchFunnel.runDetails.comparison.current')}
              </TableHead>
              <TableHead className="text-right">
                {t('batchFunnel.runDetails.comparison.previous')}
              </TableHead>
              <TableHead className="text-right">
                {t('batchFunnel.runDetails.comparison.delta')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => {
              const isDrop = row.key !== null && row.key === dropKey;
              return (
                <TableRow
                  key={row.key ?? index}
                  className={cn(isDrop && 'bg-error-50')}
                  data-testid={`batch-funnel-comparison-row-${row.key ?? String(index)}`}
                >
                  <TableCell className="text-foreground">
                    {stageLabel(row.key)}
                    {isDrop && (
                      <span
                        className="text-error ml-2 text-xs font-medium"
                        data-testid="batch-funnel-comparison-drop-badge"
                      >
                        {t('batchFunnel.runDetails.comparison.dropHere')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    className="text-right tabular-nums"
                    data-testid={`batch-funnel-comparison-current-value-${row.key ?? String(index)}`}
                  >
                    {formatFunnelCount(row.current)}
                  </TableCell>
                  <TableCell
                    className="text-muted-foreground text-right tabular-nums"
                    data-testid={`batch-funnel-comparison-previous-value-${row.key ?? String(index)}`}
                  >
                    {formatFunnelCount(row.previous)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right font-medium tabular-nums',
                      row.delta !== null && row.delta < 0 && 'text-error',
                      row.delta !== null && row.delta > 0 && 'text-success',
                    )}
                    data-testid={`batch-funnel-comparison-delta-value-${row.key ?? String(index)}`}
                  >
                    {row.delta === null
                      ? '—'
                      : `${row.delta > 0 ? '+' : ''}${row.delta.toLocaleString()}`}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <NotAvailable reason={unavailableReason} testId="batch-funnel-comparison-unavailable" />
      )}
    </Section>
  );
}

/** Per-market results — finished GMC batches with two or more markets only. */
function MarketsSection({
  funnel,
  unavailableReason,
}: {
  funnel: BatchFunnel;
  unavailableReason: string | undefined;
}) {
  const { t } = useTranslation('catalog-operations');
  const markets = funnel.markets ?? [];
  if (markets.length === 0 && !unavailableReason) return null;
  return (
    <Section title={t('batchFunnel.runDetails.markets.title')} testId="batch-funnel-markets-card">
      {markets.length > 0 ? (
        <Table data-testid="batch-funnel-markets-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('batchFunnel.runDetails.markets.market')}</TableHead>
              <TableHead className="text-right">
                {t('batchFunnel.runDetails.markets.enriched')}
              </TableHead>
              <TableHead className="text-right">
                {t('batchFunnel.runDetails.markets.enrichFailed')}
              </TableHead>
              <TableHead className="text-right">
                {t('batchFunnel.runDetails.markets.delivered')}
              </TableHead>
              <TableHead className="text-right">
                {t('batchFunnel.runDetails.markets.deliveryFailed')}
              </TableHead>
              <TableHead className="text-right">
                {t('batchFunnel.runDetails.markets.notAttempted')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {markets.map((m, index) => (
              <TableRow
                key={m.market ?? index}
                data-testid={`batch-funnel-markets-row-${m.market ?? String(index)}`}
              >
                <TableCell>
                  <p className="text-foreground font-medium">{m.market ?? '—'}</p>
                  <p className="text-muted-foreground text-xs">
                    {[m.contentLanguage, m.feedLabel].filter(Boolean).join(' · ')}
                  </p>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatFunnelCount(m.enriched)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatFunnelCount(m.enrichFailed)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatFunnelCount(m.delivered)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatFunnelCount(m.deliveryFailed)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatFunnelCount(m.notAttempted)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <NotAvailable reason={unavailableReason} testId="batch-funnel-markets-unavailable" />
      )}
    </Section>
  );
}

/** Sample run, experiment arms, consolidation and feed audit — never in the totals. */
function SideBranchesSection({
  funnel,
  feedAuditUnavailable,
}: {
  funnel: BatchFunnel;
  feedAuditUnavailable: string | undefined;
}) {
  const { t } = useTranslation('catalog-operations');
  const side = funnel.sideBranches;
  const sample = side?.sample ?? null;
  const arms = side?.arms ?? [];
  const consolidation = side?.consolidation ?? null;
  const feedAudit = side?.feedAudit ?? null;
  const hasConsolidation =
    consolidation !== null && Object.values(consolidation).some((m) => Object.keys(m).length > 0);
  if (!sample && arms.length === 0 && !hasConsolidation && !feedAudit && !feedAuditUnavailable) {
    return null;
  }
  return (
    <Section
      title={t('batchFunnel.runDetails.side.title')}
      subtitle={t('batchFunnel.runDetails.side.subtitle')}
      testId="batch-funnel-side-branches-card"
    >
      <div className="space-y-6">
        {sample && (
          <div className="space-y-2" data-testid="batch-funnel-side-sample">
            <h3 className="text-foreground text-sm font-semibold">
              {t('batchFunnel.runDetails.side.sample')}
              {sample.status && (
                <span
                  className="text-muted-foreground ml-2 text-xs font-normal"
                  data-testid="batch-funnel-side-sample-status-badge"
                >
                  {sample.status}
                </span>
              )}
            </h3>
            <FactGrid
              testId="batch-funnel-side-sample-fact"
              facts={[
                {
                  id: 'scored',
                  label: t('batchFunnel.runDetails.side.scored'),
                  value: formatFunnelCount(sample.scored),
                },
                {
                  id: 'enriched',
                  label: t('batchFunnel.runDetails.side.enriched'),
                  value: formatFunnelCount(sample.enriched),
                },
                {
                  id: 'failed',
                  label: t('batchFunnel.runDetails.side.failed'),
                  value: formatFunnelCount(sample.failed),
                },
              ]}
            />
          </div>
        )}

        {arms.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-foreground text-sm font-semibold">
              {t('batchFunnel.runDetails.side.arms')}
            </h3>
            <ul className="grid gap-2 sm:grid-cols-2" data-testid="batch-funnel-side-arms-list">
              {arms.map((arm, index) => (
                <li
                  key={arm.arm ?? index}
                  data-testid={`batch-funnel-side-arm-item-${arm.arm ?? String(index)}`}
                  className="bg-background flex items-center justify-between rounded-lg border px-3 py-2 text-sm"
                >
                  <span className="text-foreground">
                    {arm.arm ?? '—'}
                    {arm.control && (
                      <span className="text-muted-foreground ml-2 text-xs">
                        {t('batchFunnel.runDetails.side.control')}
                      </span>
                    )}
                  </span>
                  <span
                    className="font-semibold tabular-nums"
                    data-testid={`batch-funnel-side-arm-item-${arm.arm ?? String(index)}-value`}
                  >
                    {arm.assigned.toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {consolidation && hasConsolidation && (
          <div className="space-y-2" data-testid="batch-funnel-side-consolidation">
            <h3 className="text-foreground text-sm font-semibold">
              {t('batchFunnel.runDetails.side.consolidation')}
            </h3>
            <p className="text-muted-foreground text-xs">
              {t('batchFunnel.runDetails.side.consolidationHint')}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {Object.entries(consolidation).map(([group, steps]) => (
                <div
                  key={group}
                  className="bg-background rounded-lg border px-3 py-2"
                  data-testid={`batch-funnel-side-consolidation-card-${group}`}
                >
                  <p className="text-muted-foreground mb-1 text-xs font-medium">
                    {t(`batchFunnel.runDetails.side.consolidationGroup.${group}`, {
                      defaultValue: humanizeKey(group),
                    })}
                  </p>
                  <ul className="space-y-0.5 text-sm">
                    {Object.entries(steps).map(([step, count]) => (
                      <li
                        key={step}
                        className="flex justify-between gap-3"
                        data-testid={`batch-funnel-side-consolidation-item-${group}-${step}`}
                      >
                        <span className="text-foreground">
                          {t(`batchFunnel.runDetails.side.consolidationStep.${step}`, {
                            defaultValue: humanizeKey(step),
                          })}
                        </span>
                        <span className="tabular-nums">{count.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {feedAudit ? (
          <div className="space-y-2" data-testid="batch-funnel-side-feed-audit">
            <h3 className="text-foreground text-sm font-semibold">
              {t('batchFunnel.runDetails.side.feedAudit')}
            </h3>
            <FactGrid
              testId="batch-funnel-side-feed-audit-fact"
              facts={[
                {
                  id: 'audited',
                  label: t('batchFunnel.runDetails.side.audited'),
                  value: feedAudit.productCount.toLocaleString(),
                },
                {
                  id: 'succeeded',
                  label: t('batchFunnel.runDetails.side.succeeded'),
                  value: feedAudit.successCount.toLocaleString(),
                },
                {
                  id: 'failed',
                  label: t('batchFunnel.runDetails.side.failed'),
                  value: feedAudit.failedCount.toLocaleString(),
                },
                {
                  id: 'added',
                  label: t('batchFunnel.runDetails.side.added'),
                  value: feedAudit.added.toLocaleString(),
                },
                {
                  id: 'updated',
                  label: t('batchFunnel.runDetails.side.updated'),
                  value: feedAudit.updated.toLocaleString(),
                },
                {
                  id: 'removed',
                  label: t('batchFunnel.runDetails.side.removed'),
                  value: feedAudit.removed.toLocaleString(),
                },
              ]}
            />
            {Object.keys(feedAudit.productsChangedByAttribute ?? {}).length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {Object.entries(feedAudit.productsChangedByAttribute ?? {}).map(
                  ([attribute, count]) => (
                    <li
                      key={attribute}
                      className="bg-muted rounded-md px-2 py-0.5 text-xs"
                      data-testid={`batch-funnel-side-feed-audit-attribute-badge-${attribute}`}
                    >
                      {t('batchFunnel.runDetails.side.changedByAttribute', {
                        attribute,
                        count,
                      })}
                    </li>
                  ),
                )}
              </ul>
            )}
          </div>
        ) : (
          feedAuditUnavailable && (
            <div className="space-y-1">
              <h3 className="text-foreground text-sm font-semibold">
                {t('batchFunnel.runDetails.side.feedAudit')}
              </h3>
              <NotAvailable
                reason={feedAuditUnavailable}
                testId="batch-funnel-side-feed-audit-unavailable"
              />
            </div>
          )
        )}
      </div>
    </Section>
  );
}

/** People + system timeline (oldest first) and the alert events the batch publishes. */
function ActivitySection({ funnel }: { funnel: BatchFunnel }) {
  const { t } = useTranslation('catalog-operations');
  const events = funnel.events ?? [];
  const alerts = funnel.alerts;
  const alertEvents = alerts?.events ?? [];
  if (events.length === 0 && alertEvents.length === 0) return null;
  return (
    <Section title={t('batchFunnel.runDetails.activity.title')} testId="batch-funnel-activity-card">
      <div className="space-y-6">
        {events.length > 0 && (
          <ol className="space-y-3" data-testid="batch-funnel-activity-list">
            {events.map((event, index) => {
              const Icon = event.actorType === 'USER' ? User : Bot;
              return (
                <li
                  key={`${event.at ?? ''}:${event.action ?? ''}:${String(index)}`}
                  className="flex gap-3"
                  data-testid={`batch-funnel-activity-item-${String(index)}`}
                >
                  <span className="bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="text-foreground">
                      <span className="font-medium">{event.action ?? '—'}</span>
                      {event.actor && (
                        <span className="text-muted-foreground"> · {event.actor}</span>
                      )}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {[event.at ? formatDateTime(event.at) : null, event.stage]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {event.detail && (
                      <p className="text-muted-foreground text-xs break-words">{event.detail}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {alertEvents.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-foreground flex items-center gap-1.5 text-sm font-semibold">
              <Bell className="h-4 w-4" aria-hidden="true" />
              {t('batchFunnel.runDetails.activity.alerts')}
            </h3>
            <ul className="flex flex-wrap gap-1.5" data-testid="batch-funnel-activity-alerts-list">
              {alertEvents.map((code) => (
                <li
                  key={code}
                  className="bg-muted rounded-md px-2 py-0.5 font-mono text-xs"
                  data-testid={`batch-funnel-activity-alert-badge-${code}`}
                >
                  {code}
                </li>
              ))}
            </ul>
            {alerts?.note && <p className="text-muted-foreground text-xs">{alerts.note}</p>}
          </div>
        )}
      </div>
    </Section>
  );
}
